/**
 * App-open links via the environment's dedicated mobile host
 * (`GOKOLLAB_MOBILE_DOMAIN`), which serves assetlinks.json +
 * apple-app-site-association. A registered app intercepts these URLs and opens
 * at the given path (any browser, no popup); otherwise the browser loads the
 * mobile host page.
 */

export const APP_STORE_ANDROID = 'https://play.google.com/store/apps/details?id=net.clientclub.app.kollab'

function normalizePath(pathname: string): string {
  if (!pathname) return '/'
  return pathname.startsWith('/') ? pathname : `/${pathname}`
}

/**
 * https app/universal link on the environment's app-link host. Installed app →
 * opens at <path> silently in any browser (incl. Safari). Not installed → the
 * browser loads the app-link host page (which handles the iOS store redirect).
 *
 * `mobileDomain` is the environment's configured `GOKOLLAB_MOBILE_DOMAIN`
 * (m. / staging.m. / interop.m.), so each environment opens its own app.
 */
export function appLinkUrl(mobileDomain: string, pathname: string, search: string): string {
  return `https://${mobileDomain}${normalizePath(pathname)}${search || ''}`
}

/**
 * Android Intent URI using the app's custom `gokollab://` scheme. A custom
 * scheme (not https) is required: Chrome absorbs a `scheme=https` intent from a
 * JS navigation and just loads the web page instead of launching the app.
 * Chrome opens the app for `gokollab://<path>`, or navigates
 * `browser_fallback_url` (Play Store) if it isn't installed.
 */
export function androidIntentUrl(pathname: string, search: string): string {
  const path = normalizePath(pathname).replace(/^\/+/, '')
  const fallback = encodeURIComponent(APP_STORE_ANDROID)
  return `intent://${path}${search || ''}#Intent;scheme=gokollab;S.browser_fallback_url=${fallback};end`
}

/**
 * Android Intent URI that launches an installed app by package id, for apps
 * with no known custom scheme (e.g. per-location branded apps). Chrome opens
 * the app's launcher activity, or navigates `fallbackUrl` (its Play Store
 * listing) if it isn't installed. Callers must pass a validated package id.
 */
export function androidPackageIntentUrl(packageId: string, fallbackUrl: string): string {
  const fallback = encodeURIComponent(fallbackUrl)
  return `intent:#Intent;action=android.intent.action.MAIN;category=android.intent.category.LAUNCHER;package=${packageId};S.browser_fallback_url=${fallback};end`
}
